// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2026 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

/**
 * Props:
 *  * navigationOutSide: To add arrows outside swiper container, to fix overflow issue.
 *  * xController: To create custom controller.
 *  * extendedStyles: to append style to the component
 */

import SwiperCore from "swiper";
import { A11y, Autoplay, Controller, Navigation, Pagination, Scrollbar } from "swiper/modules";
import { Swiper, SwiperProps, SwiperSlide } from "swiper/react";

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import omitMultiple from "@insite/mobius/utilities/omitMultiple";
import ArrowNavigation from "@keller/App/Components/XngCarousel/ArrowNavigation";
import { xSwiperStyle } from "@keller/App/Components/XngCarousel/styles";
import { color } from "@keller/XDL/Utilities";
import { mergeWithCss } from "@keller/XDL/Utilities/mergeWithCss";
import pick from "lodash/pick";
import React, { FC, isValidElement, useEffect, useState } from "react";
import { css } from "styled-components";
import translate from "@insite/client-framework/Translate";

SwiperCore.use([Navigation, Pagination, Scrollbar, A11y, Controller, Autoplay]);

export type Props = SwiperProps & {
    extendedStyles?: SwiperStyle;
    lazy?: boolean;
};

export interface SwiperStyle {
    container?: InjectableCss;
}

// project style
const swiperStyle: SwiperStyle = {
    container: {
        css: css`
            ${xSwiperStyle}

            .xng-pagination-container {
                position: relative;
                display: flex;
                align-items: center;
                justify-content: center;
                & > div,
                & > button {
                    padding: 30px 0 0;
                }
            }

            .swiper-pagination {
                position: relative;
                text-align: center;
                display: flex;
                align-items: center;
                justify-content: center;
                flex-wrap: wrap;

                &.swiper-pagination-lock {
                    display: none;
                }

                .swiper-pagination-bullet {
                    width: 10px;
                    height: 10px;
                    margin: 8px 4px;
                    background: ${color("common.white")};
                    border: 1px solid ${color("common.ui6")};
                    opacity: 1;
                    &:hover:not(.swiper-pagination-bullet-active) {
                        background: ${color("primary")};
                    }
                    &.swiper-pagination-bullet-active {
                        width: 10px;
                        height: 10px;
                        background: ${color("common.ui16")};
                        border: 1px solid ${color("common.ui16")};
                    }
                }
            }
        `,
    },
};

const XngCarousel: FC<Props> = ({
    children,
    className,
    extendedStyles,
    slidesPerView = "auto",
    navigation = true,
    simulateTouch = false,
    spaceBetween = 20,
    pagination = { clickable: true, el: ".swiper-pagination" },
    watchOverflow = true,
    updateOnWindowResize = true,
    centerInsufficientSlides = true,
    draggable = false,
    freeMode = false,
    lazy = true,
    ...swiperProps
}) => {
    const [isSwiperCssImported, setIsSwiperCssImported] = useState(false);
    const [styles] = useState(() => mergeWithCss(swiperStyle, extendedStyles));

    useEffect(() => {
        const getStyle = async () => {
            /* eslint-disable global-require */
            // eslint-disable-next-line @typescript-eslint/no-require-imports
            await require("swiper/swiper-bundle.css");
            /* eslint-enable global-require */
        };
        getStyle().then(_ => setIsSwiperCssImported(true));
    }, []);

    if (!isSwiperCssImported) {
        return null;
    }

    return (
        <StyledWrapper {...styles.container} className={`carousel-main-wrapper ${className || ""}`}>
            <Swiper
                slidesPerView={slidesPerView}
                navigation={navigation}
                simulateTouch={simulateTouch}
                spaceBetween={spaceBetween}
                pagination={pagination}
                watchOverflow={watchOverflow}
                updateOnWindowResize={updateOnWindowResize}
                centerInsufficientSlides={centerInsufficientSlides}
                draggable={draggable}
                freeMode={freeMode}
                {...swiperProps}
            >
                {React.Children.map(children, child => {
                    // for null case
                    if (!isValidElement(child)) {
                        return;
                    }

                    const props = child.props as Record<string, unknown>;
                    const swiperSlidePropsKeys = ["tag", "zoom", "virtualIndex"];
                    const childrenProps = omitMultiple(props, [...swiperSlidePropsKeys]);
                    const swiperSlideProps = pick(props, swiperSlidePropsKeys);

                    return (
                        <SwiperSlide {...swiperSlideProps}>
                            {React.createElement(child.type, childrenProps)}
                            {lazy && <div className="swiper-lazy-preloader"></div>}
                        </SwiperSlide>
                    );
                })}

                {(pagination || navigation || swiperProps.scrollbar) && (
                    <StyledWrapper className="xng-pagination-container">
                        {navigation && (
                            <ArrowNavigation arrowType="prev" showOnMobile={true} aria-label={translate("Previous")} />
                        )}
                        {pagination && <div className="swiper-pagination" />}
                        {swiperProps.scrollbar && <div className="swiper-scrollbar" />}
                        {navigation && (
                            <ArrowNavigation arrowType="next" showOnMobile={true} aria-label={translate("Next")} />
                        )}
                    </StyledWrapper>
                )}
            </Swiper>
        </StyledWrapper>
    );
};

export default XngCarousel;
